/**
 * AuthContext — gerencia sessão de autenticação Supabase + modo demo.
 *
 * Em modo demo, um "login" fake é feito apenas no localStorage para permitir
 * o fluxo completo sem depender do Supabase.
 */

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { getSupabase, IS_DEMO_MODE } from '../lib/supabase'
import { LS_VET, LS_AVATAR, LS_ANIMALS, LS_NOTIF } from '../lib/storage-keys'

interface SimpleUser {
  id: string
  email: string
  fullName: string
  hasFullName: boolean
}

interface AuthContextValue {
  user: SimpleUser | null
  loading: boolean
  isDemoMode: boolean
  signIn: (email: string, password: string) => Promise<{ error: string | null }>
  signUp: (
    email: string,
    password: string,
    fullName: string
  ) => Promise<{ error: string | null; autoLoggedIn: boolean }>
  signOut: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

const DEMO_USER_KEY = 'florasegura:demo-user'

/**
 * Dados pessoais que moram no DISPOSITIVO (localStorage), nao na conta.
 * Precisam ser descartados quando o usuario logado muda, senao a proxima
 * conta herda animais, veterinario e avatar da anterior.
 *
 * Isso e critico: o vet cadastrado alimenta a tela de Emergencia. Herdar o
 * vet de outra pessoa faz o app mandar ligar para o veterinario errado.
 *
 * Correcao definitiva (backlog): mover esses dados para o Supabase por
 * user_id, para acompanharem a pessoa entre dispositivos.
 */
const LAST_USER_KEY = 'florasegura:last-user-id'
const DEVICE_PERSONAL_KEYS = [LS_VET, LS_AVATAR, LS_ANIMALS, LS_NOTIF]

function clearDevicePersonalData() {
  DEVICE_PERSONAL_KEYS.forEach((k) => {
    try { localStorage.removeItem(k) } catch { /* storage indisponivel */ }
  })
}

/** Limpa os dados do dispositivo sempre que o usuario da sessao muda. */
function guardDeviceData(userId: string | null) {
  try {
    const prev = localStorage.getItem(LAST_USER_KEY)
    if (!userId) {
      clearDevicePersonalData()
      localStorage.removeItem(LAST_USER_KEY)
      return
    }
    // prev null = nao sabemos de quem sao os dados que estao no device.
    // Na duvida, descartar: mostrar o vet errado numa emergencia e pior do
    // que a pessoa recadastrar. Sem isso, o primeiro login apos o deploy
    // deste guard herdaria os dados da conta anterior.
    if (prev !== userId) clearDevicePersonalData()
    localStorage.setItem(LAST_USER_KEY, userId)
  } catch { /* storage indisponivel */ }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SimpleUser | null>(null)
  const [loading, setLoading] = useState(true)

  // ---- Bootstrap sessão ----
  useEffect(() => {
    let mounted = true

    async function bootstrap() {
      if (IS_DEMO_MODE) {
        try {
          const raw = localStorage.getItem(DEMO_USER_KEY)
          if (raw && mounted) setUser(JSON.parse(raw))
        } catch {
          /* ignora */
        }
        if (mounted) setLoading(false)
        return
      }

      const supabase = getSupabase()!
      const { data } = await supabase.auth.getSession()
      if (mounted && data.session?.user) {
        const u = data.session.user
        const metaFullName = u.user_metadata?.full_name as string | undefined
        setUser({
          id: u.id,
          email: u.email ?? '',
          fullName: metaFullName ?? u.email?.split('@')[0] ?? '',
          hasFullName: !!metaFullName,
        })
      }
      if (mounted) setLoading(false)

      // Listener para mudanças de auth
      supabase.auth.onAuthStateChange((_event, session) => {
        if (!mounted) return
        // troca de usuario (login, signup ou logout) descarta dados do device
        guardDeviceData(session?.user?.id ?? null)
        if (session?.user) {
          const metaFullName = session.user.user_metadata?.full_name as string | undefined
          setUser({
            id: session.user.id,
            email: session.user.email ?? '',
            fullName: metaFullName ?? session.user.email?.split('@')[0] ?? '',
            hasFullName: !!metaFullName,
          })
        } else {
          setUser(null)
        }
      })
    }

    bootstrap()
    return () => {
      mounted = false
    }
  }, [])

  // ---- Sign in ----
  const signIn = useCallback(async (email: string, password: string) => {
    if (IS_DEMO_MODE) {
      if (!email || !password) {
        return { error: 'Informe email e senha.' }
      }
      const demoUser: SimpleUser = {
        id: `demo-${email}`,
        email,
        fullName: email.split('@')[0],
        hasFullName: false,
      }
      localStorage.setItem(DEMO_USER_KEY, JSON.stringify(demoUser))
      setUser(demoUser)
      return { error: null }
    }

    const supabase = getSupabase()!
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    return { error: error?.message ?? null }
  }, [])

  // ---- Sign up ----
  const signUp = useCallback(
    async (email: string, password: string, fullName: string) => {
      if (IS_DEMO_MODE) {
        const demoUser: SimpleUser = {
          id: `demo-${email}`,
          email,
          fullName: fullName || email.split('@')[0],
          hasFullName: !!fullName,
        }
        localStorage.setItem(DEMO_USER_KEY, JSON.stringify(demoUser))
        setUser(demoUser)
        return { error: null, autoLoggedIn: true }
      }

      const supabase = getSupabase()!
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { full_name: fullName } },
      })

      if (error) return { error: error.message, autoLoggedIn: false }

      // Se a confirmação de e-mail estiver desativada no Supabase,
      // o signUp já retorna uma sessão ativa → loga automaticamente.
      if (data.session?.user) {
        const u = data.session.user
        setUser({
          id: u.id,
          email: u.email ?? '',
          fullName: (u.user_metadata?.full_name as string) ?? fullName ?? u.email?.split('@')[0] ?? '',
          hasFullName: !!((u.user_metadata?.full_name as string) ?? fullName),
        })
        return { error: null, autoLoggedIn: true }
      }

      // Confirmação de e-mail ainda ativa: usuário precisa confirmar
      return { error: null, autoLoggedIn: false }
    },
    []
  )

  // ---- Sign out ----
  const signOut = useCallback(async () => {
    if (IS_DEMO_MODE) {
      localStorage.removeItem(DEMO_USER_KEY)
      setUser(null)
      return
    }
    const supabase = getSupabase()!
    await supabase.auth.signOut()
    guardDeviceData(null)
    setUser(null)
  }, [])

  const refreshUser = useCallback(async () => {
    if (IS_DEMO_MODE) return
    const sb = getSupabase()
    if (!sb) return
    const { data } = await sb.auth.getUser()
    if (!data.user) return
    const u = data.user
    const metaFullName = u.user_metadata?.full_name as string | undefined
    setUser({
      id: u.id,
      email: u.email ?? '',
      fullName: metaFullName ?? u.email?.split('@')[0] ?? '',
      hasFullName: !!metaFullName,
    })
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      isDemoMode: IS_DEMO_MODE,
      signIn,
      signUp,
      signOut,
      refreshUser,
    }),
    [user, loading, signIn, signUp, signOut, refreshUser]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth deve ser usado dentro de <AuthProvider>')
  }
  return ctx
}
